// ─────────────────────────────────────────────────────────────────────────
// Types for the public, token-based Supplier Approval page.
// Mirrors GET /booking/supplier-approval?order_id=&supplier_id=&token=
//   → { status, data: { order_number, supplier_name, notes, rooms:[...] } }
// token = sha1(order_id:supplier_id:SUPPLIER_APPROVAL_SECRET), verified
// server-side on every request — nothing is stored in the DB per-link.
// ─────────────────────────────────────────────────────────────────────────

export type GuestType = 'adult' | 'child' | 'infant';

export interface SupplierApprovalGuest {
  firstName: string;
  lastName: string;
  type: GuestType;
  age?: number;
}

export interface SupplierApprovalRoom {
  orderProductId: number;
  hotelName: string;
  roomName: string;
  amount: number;
  checkIn: string; // "DD/MM/YYYY"
  checkOut: string; // "DD/MM/YYYY"
  nights: number;
  board?: string;
  supplierNetPrice: number;
  currencySign: string;
  paxSummary: string;
  guests: SupplierApprovalGuest[];
}

export interface SupplierApprovalFlightLeg {
  flightNumber?: string;
  airlineName?: string;
  fromCode?: string;
  fromName?: string;
  toCode?: string;
  toName?: string;
  dateTime?: string; // "DD/MM/YYYY HH:mm"
}

export interface SupplierApprovalTransferInfo {
  pickupDateTime?: string; // "DD/MM/YYYY HH:mm"
  dropoffDateTime?: string; // "DD/MM/YYYY HH:mm"
}

// Only present when this supplier also handles the order's transfer — they
// need the guest's flight times to plan pickup/drop-off. Absent/both legs
// undefined means there's nothing to show.
export interface SupplierApprovalFlightDetails {
  arrivalFlight?: SupplierApprovalFlightLeg;
  departureFlight?: SupplierApprovalFlightLeg;
  transfer?: SupplierApprovalTransferInfo;
}

export interface SupplierApprovalData {
  message?: string;
  order_id?: number;
  order_number?: number | string;
  supplier_id?: number;
  supplier_name?: string;
  notes?: string | null;
  // The booking customer's contact phone — guests don't carry their own
  // phone number for hotel bookings, so this is the one number to reach
  // out on if the supplier needs to contact someone about the stay.
  customer_phone?: string | null;
  rooms?: SupplierApprovalRoom[];
  flightDetails?: SupplierApprovalFlightDetails | null;
  // Set when the supplier has already reviewed/approved this order. The backend
  // may signal it as a boolean flag or as data.status === 'already_approved'
  // (in which case rooms/supplier are omitted).
  already_approved?: boolean;
  status?: 'already_approved' | string;
}

export interface SupplierApprovalResponse {
  status: 'success' | 'error' | 'already_approved';
  message?: string;
  already_approved?: boolean;
  data?: SupplierApprovalData;
}

// ── Per-room supplier input state (client-side only) ──────────────────────

export type PriceMode = 'approve' | 'different';
export type RoomAction = 'approved' | 'not_approved';

export interface RoomInputState {
  ts_pnr: string;
  priceMode: PriceMode;
  suggested_supplier_net_price: string; // kept as string for the input field
  action?: RoomAction;
}

// Status/price_status values persisted to the backend on submit.
export const SUPPLIER_STATUS = {
  APPROVED: 'supplier_approved',
  PRICE_CHANGED: 'supplier_price_changed',
} as const;

export const PRICE_STATUS = {
  APPROVED: 'approved',
  SUGGESTED: 'supplier_suggested_price',
} as const;

// ── Helpers ───────────────────────────────────────────────────────────────

/** Full guest name, or an empty string when the backend sends blanks. */
export const getGuestName = (g: SupplierApprovalGuest): string =>
  [g.firstName, g.lastName].filter(Boolean).join(' ').trim();
